import { useCallback, useReducer } from "react";
import { BundleTypes, DISCOUNT_TYPES, generateRandomUniqueId, generateUniqueId } from "../constants";
import { Action, PlaceholderConfigInterface, PlaceholderStepsType, ChangeEvents, SubStepType } from "../Types";

export const DEFAULT_STEP: PlaceholderStepsType = {
    step_label: "Choose Step 1 Product(s)",
    page_title: "",
    page_subtitle: "",
    step_icon: null,
    step_content_type: "products",
    product_selection: "specific",
    enable_subSteps: false,
    subSteps: [],
    products: [],
    collections: [],
    step_conditions: [
        {
            basis: "items_count",
            type: "exact_products",
            limit: 5,
            msg: "Select exact {productCount} products",
        },
    ],
    render_as_variants: false,
    show_step_conditions: false,
    show_banner: false,
    banner_desktop: "",
    banner_mobile: "",
};

export const DEFAULT_SUBSTEP: SubStepType = {
    step_label: "",
    product_selection: "specific",
    products: [],
    collections: [],
    subStep_condition_basis: "items_count",
    subStep_condition_type: "exact_products",
    product_count_limit: 1,
    step_error_msg: "Select exact {productCount} products",
};

export const initialConfigData: PlaceholderConfigInterface = {
    status: false,
    bundle_type: BundleTypes.BB_PLACEHOLDER,
    bundle_as_product: {
        product_title: "",
        product_description: "",
        images: [
            {
                id: generateRandomUniqueId(),
                originalSource:
                    "https://kefiimages.blob.core.windows.net/kefiimages/9ff74a06-c31e-4825-8c30-a8e4af790f52.png",
                alt: "img",
                mediaContentType: "IMAGE",
            },
        ],
        show_on_product_page: [],
        product_handle: "",
        double_discount: false,
        collections: [],
    },
    isProductPageVisible: false,
    allow_pre_selected_products: false,
    pre_selected_products: [],
    make_pre_selected_compulsory: true,
    product_card_desktop_size: "small",
    product_card_mobile_size: "small",
    allow_conditional_logic: false,
    product_conditions: [],
    redirect_options: "cart",
    show_popup_on_cart_redirect: false,
    popup_text: "<p>Yay, your bundle has been added to your cart.</p>",
    popup_redirect_url: "",

    // design from
    bundles_design: {
        default_cta: false,
        bundle_title_size: 22,
        bundle_title_color: "#000000",
        bundle_title_style: "650 normal",
        product_name_size: 14,
        product_name_color: "#050505",
        product_name_style: "450 normal",
        product_price_size: 12,
        product_price_color: "#050505",
        product_price_style: "350 normal",
        border_color: "#050505",
        border_width: 1,
        border_radius: 8,
        background_color: "#F2F4F2",
        highlighted_offer_tag_size: 12,
        highlighted_offer_tag_color: "#F8F8F8",
        highlighted_offer_tag_style: "550 italic",
        highlighted_price_tag_size: 14,
        highlighted_price_tag_color: "#000000",
        highlighted_price_tag_style: "550 normal",
        total_footer_text_size: 14,
        total_footer_text_color: "#303030",
        total_footer_text_style: "550 normal",
        total_footer_price_text_size: 16,
        total_footer_price_text_color: "#000000",
        total_footer_price_text_style: "750 normal",
        total_footer_background_color: "#ffffff",
        total_footer_background_style: "450 normal",
        product_background_color: "#FFFFFF",
        product_border_color: "#F2F2F2",
        add_to_cart_bg_color: "#303030",
        add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#FFFFFF",
        add_to_cart_text_color: "#F5F5F5",
        add_to_cart_text_size: 14,
        add_to_cart_text_style: "750 normal",
        bundle_widget_bg: "#f4f4f4",
        bundle_widget_font_family: "Custom Store Font",
        product_img_size: "medium",
        product_custom_img_size: 160,
        product_custom_img_size_mobile: 160,
        product_img_show: "all",
        product_img_border_radius: 10,
        free_gift_header_bg_color: "#E3E3E3",
        free_gift_header_color: "#303030",
        free_gift_header_size: 13,
        free_gift_header_style: "650 normal",
        free_gift_bg_color: "#E0E0E0",
        free_gift_text_color: "#303030",
        free_gift_text_size: 12,
        free_gift_text_style: "650 normal",
        free_gift_border: true,
        free_gift_border_width: 0,
        free_gift_border_color: "#441144",
        product_img_border_width: 1,
        product_img_border_color: "#CCCCCC",
        variant_selector_text_size: 12,
        variant_selector_text_color: "#383838",
        variant_selector_text_style: "450 normal",
        secondary_cta_bg_color: "#0d0d0d",
        secondary_cta_text_color: "#ffffff",
        tertiary_cta_bg_color: "#f2f2f2",
        tertiary_cta_text_color: "#000000",
        step_label_color: "#3F3F3F",
        step_label_style: "550 normal",
        step_label_size: 16,
        step_page_title_color: "#000000",
        step_page_title_size: 16,
        step_page_title_style: "650 normal",
        step_page_subtitle_color: "#3F3F3F",
        step_page_subtitle_size: 13,
        step_page_subtitle_style: "450 normal",
        order_summary_header_text_color: "#222222",
        order_summary_header_text_size: 15,
        order_summary_header_text_style: "550 normal",
        order_summary_rows_text_color: "#000000b2",
        order_summary_rows_text_size: 12,
        order_summary_rows_text_style: "450 normal",
        order_summary_bg_color: "#EDEDED",
        edit_bundle_cta_bg_color: "#afacac3b",
        edit_bundle_cta_text_color: "#424242",
        edit_bundle_cta_style: "450 normal",
        edit_bundle_cta_size: 14,
        bb_margin_top: 0,
        popup_cta_bg_color: "#030303",
        special_text_background_color: "#FFDC51",
        special_text_color: "#303030",
        special_text_font_size: 12,
        special_text_style: "650 normal",
        added_quantity_bg_color: "#000000",
        added_quantity_text_color: "#ffffff",
        added_quantity_text_style: "550 normal",
        added_quantity_text_size: 12,
        modal_footer_bg: "#222222",
        dotted_border_color: "#000000",
        step_container_bg: "#f4f4f4",
    },
    bundle_name: "",
    title: "Create Your Own Bundle",
    description: "",
    discount_type: "percentage",
    discount_value: 5,
    tier_discount_basis: "items_count",
    hide_prices_product_card: false,
    products: [],
    steps: [{ ...DEFAULT_STEP }],
    free_gift_step: {
        is_enabled: false,
        page_title: "",
        page_subtitle: "",
        step_label: "Add Free Gift",
        step_icon: null,
        products: [],
        enable_subSteps: false,
        step_content_type: "products",
        product_selection: "specific",
        collections: [],
        step_condition_type: "exact_products",
        product_count_limit: 1,
        step_error_msg: "Select maximum {productCount} products",
        render_as_variants: true,
        show_banner: false,
        banner_desktop: "",
        banner_mobile: "",
    },
    default_step: { ...DEFAULT_STEP },
    bundle_product_level: "product",
    hide_native_elements: "",
    add_to_cart_cta_text: "Add to Cart",
    add_to_cart_cta_hover_text: "Products are required.",
    quantity_text: "Qty",
    footer_total_text: "Total",
    footer_text: "Off",
    free_text: "Free",
    buy_now_cta_text: "+ Add",
    edit_cta_text: "Add Product",
    bundle_ready_text: "Your {title} Bundle is ready!",
    next_cta_text: "Next",
    previous_cta_text: "Previous",
    checkout_cta_text: "Go to Checkout",
    done_cta_text: "Done",
    out_of_stock_text: "Out of Stock!",
    order_summary_title: "Order Summary",
    order_summary_subtotal: "Subtotal",
    order_summary_discount: "Discount",
    tooltip_message: "Products are required.",
    start_date: new Date().toISOString(),
    end_date: null,
    use_compare_at_price: false,
    allow_free_gifts: false,
    free_gifts: [],
    free_gift_text: "+ Free Gift",
    bundle_embed_id: generateRandomUniqueId(),
    show_swatches: false,
    apply_discount_on_compare_price: false,
    no_close_accordian_after_completion: false,
    offer_name: "",
    // @ts-expect-error
    selected_template_placeholder: "", // Keeping it empty for template selection at start

    swatchOptions: [],
    swatchesProductsMap: {},
    swatchShape: "circle",
    swatchSize: 18,
    offer_cta_text: "Build Your Box",
    limit_show_offer_cta: "all",
    show_offer_cta_pages: [],
    offer_cta_position: "below_cart",
    discount_options: [
        {
            id: generateUniqueId(),
            discountType: DISCOUNT_TYPES.PERCENTAGE,
            discountValue: 10,
            quantity: 2,
            maxQuantity: 2,
            addRange: true,
            ShowTierSuccessMessage: true,
            TierSuccessMessage: "Add {noOfRemainingProducts} more product(s) to get {discount} discount!",
            showFinalSuccessMessage: true,
            finalSuccessMessage: "Congratulations! You've got the best offer on your bundle.",
            selected_products: [],
        },
        {
            id: generateUniqueId(),
            discountType: DISCOUNT_TYPES.PERCENTAGE,
            discountValue: 20,
            quantity: 3,
            maxQuantity: 3,
            addRange: true,
            ShowTierSuccessMessage: true,
            TierSuccessMessage: "Add {noOfRemainingProducts} more product(s) to get {discount} discount!",
            selected_products: [],
        },
    ],
    show_progress_bar: true,
    // target from
    targeting: {
        includeCustomers: "all",
        excludeCustomers: "none",
        includeCustomerData: "",
        excludeCustomerData: "",
        shopifyMarketsTargeting: "all",
        includeLocations: [],
        excludeLocations: [],
        shopifyMarketsTargetingData: [],
        //newKeys
        targetingType: "all",
        customerTagsEnabled: false,
        customerTagType: "include",
        customerTags: "",
        customerSpentEnabled: false,
        customerSpentType: "greater_than",
        customerSpent: "",
        customerCountriesEnabled: false,
        customerCountryType: "include",
        customerCountries: [],
    },
    hide_out_of_stock_products: false,
    show_search_bar: false,
    limit_max_quantity_per_product: false,
    max_quantity_per_product: 5,
    move_selected_products_first: true,
    accordion_product_layout: "scroll",

    // Settings
    general_settings: {
        show_bundle_on_product_page: false,
        show_multiple_bundles_on_product_page: false,
        redirect_after_adding_product_to_cart: "",
        redirect_to_checkout: false,
        show_powered_by_kefi: true,
        max_width: 1000,
        bundle_alignment: "flex-start",
        override_max_width: false,
    },
    bundles_settings: {
        enable_stock_control: false,
        out_of_stock_text: "Out of stock",
        listing_variant_with_image: false,
        work_only_with_widget: false,
        bundle_position: "below_cart",
        custom_selector: "",
        custom_css: "",
        custom_js: "",
    },
    show_bundle_widget: "show_everywhere",
    free_shipping: false,
    animation_add_to_cart_btn: "none",
    order_discounts: true,
    product_discounts: true,
    shipping_discounts: true,
};

const useStandardHook = () => {
    const bundleConfigReducer = useCallback(
        (state: PlaceholderConfigInterface, action: Action<PlaceholderConfigInterface>): PlaceholderConfigInterface => {
            switch (action?.type) {
                case ChangeEvents.initState:
                    return { ...state, ...action.init };
                default:
                    if (action?.key && action.isComplex) {
                        return {
                            ...state,
                            [action?.key]: {
                                ...(state[action.key] as object),
                                ...(action.payload as object),
                            },
                        };
                    } else if (action?.key) {
                        return { ...state, [action?.key]: action.payload };
                    } else return { ...state };
            }
        },
        [],
    );

    const [bundleConfig, dispatchBundleConfig] = useReducer(bundleConfigReducer, initialConfigData);

    return {
        bundleConfig,
        dispatchBundleConfig,
    };
};

export default useStandardHook;
